import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import './ModalSystem.css';

// ============================================================================
// Modal Context - provides show/hide methods to entire app
// ============================================================================
const ModalContext = createContext(null);

export function useModal() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within ModalProvider');
  return ctx;
}

export function ModalProvider({ children }) {
  const [modals, setModals] = useState([]);
  const idCounter = useRef(0);

  const removeModal = useCallback((id) => {
    setModals(prev => prev.filter(m => m.id !== id));
  }, []);

  const showAlert = useCallback((message, { title = 'Notice', type = 'info' } = {}) => {
    return new Promise((resolve) => {
      const id = ++idCounter.current;
      setModals(prev => [...prev, {
        id, kind: 'alert', title, message, type,
        onClose: () => { removeModal(id); resolve(); }
      }]);
    });
  }, [removeModal]);

  const showConfirm = useCallback((message, { title = 'Confirm', type = 'warning', confirmText = 'OK', cancelText = 'Cancel' } = {}) => {
    return new Promise((resolve) => {
      const id = ++idCounter.current;
      setModals(prev => [...prev, {
        id, kind: 'confirm', title, message, type, confirmText, cancelText,
        onConfirm: () => { removeModal(id); resolve(true); },
        onCancel: () => { removeModal(id); resolve(false); }
      }]);
    });
  }, [removeModal]);

  const showInput = useCallback((message, { title = 'Input', defaultValue = '', placeholder = '', type = 'text' } = {}) => {
    return new Promise((resolve) => {
      const id = ++idCounter.current;
      setModals(prev => [...prev, {
        id, kind: 'input', title, message, defaultValue, placeholder, inputType: type,
        onSubmit: (value) => { removeModal(id); resolve(value); },
        onCancel: () => { removeModal(id); resolve(null); }
      }]);
    });
  }, [removeModal]);

  const value = { showAlert, showConfirm, showInput };

  return (
    <ModalContext.Provider value={value}>
      {children}
      <div className="modal-portal">
        {modals.map(modal => (
          <ModalRenderer key={modal.id} modal={modal} />
        ))}
      </div>
    </ModalContext.Provider>
  );
}

// ============================================================================
// Modal Renderer - renders the correct modal type
// ============================================================================
function ModalRenderer({ modal }) {
  switch (modal.kind) {
    case 'alert': return <AlertModal {...modal} />;
    case 'confirm': return <ConfirmModal {...modal} />;
    case 'input': return <InputModal {...modal} />;
    default: return null;
  }
}

// ============================================================================
// Overlay wrapper
// ============================================================================
function ModalOverlay({ children, onClose }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="modal-overlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current && onClose) onClose(); }}
    >
      <div className="modal-dialog" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

// ============================================================================
// Type icon
// ============================================================================
function TypeIcon({ type }) {
  const icons = {
    info: { symbol: 'i', className: 'modal-icon-info' },
    success: { symbol: '\u2713', className: 'modal-icon-success' },
    warning: { symbol: '!', className: 'modal-icon-warning' },
    error: { symbol: '\u2715', className: 'modal-icon-error' }
  };
  const icon = icons[type] || icons.info;
  return <div className={`modal-icon ${icon.className}`}>{icon.symbol}</div>;
}

// ============================================================================
// Alert Modal
// ============================================================================
function AlertModal({ title, message, type, onClose }) {
  const btnRef = useRef(null);
  useEffect(() => { btnRef.current?.focus(); }, []);

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-header">
        <TypeIcon type={type} />
        <h3 className="modal-title">{title}</h3>
      </div>
      <div className="modal-body">
        <p className="modal-message">{typeof message === 'string' ? message : message}</p>
      </div>
      <div className="modal-footer">
        <button ref={btnRef} className="modal-btn modal-btn-primary" onClick={onClose}>OK</button>
      </div>
    </ModalOverlay>
  );
}

// ============================================================================
// Confirm Modal
// ============================================================================
function ConfirmModal({ title, message, type, confirmText, cancelText, onConfirm, onCancel }) {
  const confirmRef = useRef(null);
  useEffect(() => { confirmRef.current?.focus(); }, []);

  return (
    <ModalOverlay onClose={onCancel}>
      <div className="modal-header">
        <TypeIcon type={type} />
        <h3 className="modal-title">{title}</h3>
      </div>
      <div className="modal-body">
        <p className="modal-message">{message}</p>
      </div>
      <div className="modal-footer">
        <button className="modal-btn modal-btn-secondary" onClick={onCancel}>{cancelText}</button>
        <button ref={confirmRef} className="modal-btn modal-btn-primary" onClick={onConfirm}>{confirmText}</button>
      </div>
    </ModalOverlay>
  );
}

// ============================================================================
// Input Modal
// ============================================================================
function InputModal({ title, message, defaultValue, placeholder, inputType, onSubmit, onCancel }) {
  const [value, setValue] = useState(defaultValue || '');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSubmit = () => {
    if (value.trim()) onSubmit(value.trim());
  };

  return (
    <ModalOverlay onClose={onCancel}>
      <div className="modal-header">
        <TypeIcon type="info" />
        <h3 className="modal-title">{title}</h3>
      </div>
      <div className="modal-body">
        {message && <p className="modal-message">{message}</p>}
        <input
          ref={inputRef}
          type={inputType || 'text'}
          className="modal-input"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={placeholder}
          onKeyDown={e => {
            if (e.key === 'Enter' && value.trim()) handleSubmit();
          }}
        />
      </div>
      <div className="modal-footer">
        <button className="modal-btn modal-btn-secondary" onClick={onCancel}>Cancel</button>
        <button
          className="modal-btn modal-btn-primary"
          onClick={handleSubmit}
          disabled={!value.trim()}
        >
          OK
        </button>
      </div>
    </ModalOverlay>
  );
}

export default ModalProvider;
